import {
    IMPERSONATION_BACKUP_KEY,
    IMPERSONATION_STATE_KEY,
    USER_LOCALSTORAGE_KEY,
} from "@/shared/config/localstorage";
import type { Identity } from "./types";

export const R2_TOKEN_KEY = "r2_session_token";
// Выход не стирает r2_session_token, а вход через Telegram или с неудавшимся
// обменом токена нового не кладёт. Без этой отметки события следующего
// человека ушли бы с токеном прежнего — и легли бы в его журнал.
const RETIRED_KEY = "activity_retired_r2_v1";

/** Ключи, смена которых в другой вкладке меняет владельца журнала. */
export const IDENTITY_KEYS: ReadonlySet<string> = new Set([
    USER_LOCALSTORAGE_KEY,
    R2_TOKEN_KEY,
    RETIRED_KEY,
    "site_user_email",
    IMPERSONATION_STATE_KEY,
    IMPERSONATION_BACKUP_KEY,
]);

const CACHE_MS = 1_000;
let cache: { at: number; id: Identity } | null = null;
const blockedOwners = new Set<string>();

// Отпечаток вместо самого токена: второй копии секрета в хранилище не нужно.
const fingerprint = (s: string): string => {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return `${(h >>> 0).toString(16)}.${s.length}`;
};

const retiredList = (): string[] => {
    try {
        const v = JSON.parse(localStorage.getItem(RETIRED_KEY) || "[]") as unknown;
        return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    } catch {
        return [];
    }
};

/** Токен остался от прежнего владельца: журнал им больше не пользуется. */
export const retireToken = (token: string | null | undefined): void => {
    if (!token) return;
    try {
        const f = fingerprint(token);
        const list = retiredList();
        if (!list.includes(f)) {
            localStorage.setItem(RETIRED_KEY, JSON.stringify([f, ...list].slice(0, 8)));
        }
    } catch {
        /* best effort */
    }
    cache = null;
};

/**
 * enterImpersonation кладёт резервную копию сессии админа ДО finalizeEmailAuth,
 * а флаг impersonation_state — только после. В этом промежутке isImpersonating()
 * ещё false, поэтому смотрим и на резервную копию.
 */
export const impersonationActive = (): boolean => {
    try {
        if (localStorage.getItem(IMPERSONATION_BACKUP_KEY)) return true;
        const raw = localStorage.getItem(IMPERSONATION_STATE_KEY);
        if (!raw) return false;
        try {
            return !!(JSON.parse(raw) as { active?: boolean } | null)?.active;
        } catch {
            return true; // испорченный флаг — лучше промолчать, чем писать под чужим именем
        }
    } catch {
        return false;
    }
};

const readOwner = (): string | null => {
    const raw = localStorage.getItem(USER_LOCALSTORAGE_KEY);
    if (!raw) return null;
    let email = "";
    try {
        const u = JSON.parse(raw) as unknown;
        if (typeof u === "string") email = u;
        else if (u && typeof u === "object") {
            const o = u as { email?: unknown; id?: unknown };
            email =
                String(o.email || "") ||
                localStorage.getItem("site_user_email") ||
                String(o.id || "");
        }
    } catch {
        email = raw;
    }
    email = email.trim().toLowerCase();
    return email || null;
};

const compute = (): Identity => {
    try {
        const email = readOwner();
        // Только токен основного воркера: auth_token и personal_data могут
        // оказаться непомеченной сессией «войти как».
        const token = localStorage.getItem(R2_TOKEN_KEY) || null;
        return {
            email,
            token: token && !retiredList().includes(fingerprint(token)) ? token : null,
            suspended: impersonationActive() || (!!email && blockedOwners.has(email)),
        };
    } catch {
        return { email: null, token: null, suspended: true };
    }
};

export const readIdentity = (now: number = Date.now()): Identity => {
    if (cache && now - cache.at < CACHE_MS) return cache.id;
    const id = compute();
    cache = { at: now, id };
    return id;
};

export const invalidateIdentity = (): void => {
    cache = null;
};

/** После удаления аккаунта в этой вкладке его журнал больше не ведём. */
export const blockOwner = (email: string): void => {
    const e = email.trim().toLowerCase();
    if (e) blockedOwners.add(e);
    invalidateIdentity();
};
